import { mergeAttributes, Node } from '@tiptap/core';
import type { Range } from '@tiptap/core';
import { PluginKey } from '@tiptap/pm/state';
import { ReactNodeViewRenderer, ReactRenderer } from '@tiptap/react';
import type { NodeViewProps } from '@tiptap/react';
import Suggestion from '@tiptap/suggestion';
import type { SuggestionProps } from '@tiptap/suggestion';

import type { CommentMentionKind, CommentMentionTarget, ResourceActivityResourceType } from '@/types';

import CommentMentionNodeView from './CommentMentionNodeView';
import insertCommentMention from './insertCommentMention';
import MentionSuggestionList, { MentionSuggestionListHandle } from './MentionSuggestionList';
import { serializeCommentMention } from './serialization';

type WorkspaceMentionNodeOptions = {
    hoverable?: boolean;
    htmlAttribute?: string;
    interactive?: boolean;
    name?: string;
    publicMemoId?: string;
    publicWorkspaceMetadata?: boolean;
    resourceId: string;
    resourceType: ResourceActivityResourceType;
};

export const createWorkspaceMentionNode = (options: WorkspaceMentionNodeOptions) => {
    const htmlAttribute = options.htmlAttribute ?? 'data-workspace-mention';
    const name = options.name ?? 'workspaceMention';
    const pluginKey = new PluginKey(`${name}Suggestion`);

    return Node.create({
        name,
        group: 'inline',
        inline: true,
        atom: true,
        draggable: false,
        selectable: false,

        addAttributes() {
            return {
                avatar: { default: null },
                handle: { default: null },
                href: { default: null },
                id: { default: null },
                key: { default: null },
                kind: { default: 'member' as CommentMentionKind },
                label: { default: '' },
                resourceType: { default: null },
                updatedAt: { default: null },
                userId: { default: null },
                website: { default: null },
            };
        },

        parseHTML() {
            return [{ tag: `span[${htmlAttribute}]` }];
        },

        renderHTML({ HTMLAttributes }) {
            return [
                'span',
                mergeAttributes(HTMLAttributes, {
                    [htmlAttribute]: 'true',
                }),
                `@${HTMLAttributes.label ?? ''}`,
            ];
        },

        renderText({ node }) {
            return serializeCommentMention(node.attrs as CommentMentionTarget);
        },

        addNodeView() {
            const MentionNodeView = (props: NodeViewProps) => (
                <CommentMentionNodeView
                    {...props}
                    hoverable={options.hoverable}
                    interactive={options.interactive}
                    publicMemoId={options.publicMemoId}
                    publicWorkspaceMetadata={options.publicWorkspaceMetadata}
                />
            );

            return ReactNodeViewRenderer(MentionNodeView);
        },

        addProseMirrorPlugins() {
            return [
                Suggestion<CommentMentionTarget, CommentMentionTarget>({
                    editor: this.editor,
                    char: '@',
                    pluginKey,
                    items: () => [],
                    command: ({ editor, props, range }) =>
                        insertCommentMention(editor, range as Range, props, this.name),
                    render: () => {
                        let component: ReactRenderer<
                            MentionSuggestionListHandle,
                            SuggestionProps<CommentMentionTarget, CommentMentionTarget> & WorkspaceMentionNodeOptions
                        > | null = null;
                        let unmount: (() => void) | undefined;
                        let currentProps: SuggestionProps<CommentMentionTarget, CommentMentionTarget> | null = null;

                        const applyPosition = (
                            element: HTMLElement,
                            position: { strategy: 'absolute' | 'fixed'; x: number; y: number },
                        ) => {
                            const anchor = currentProps?.clientRect?.();
                            const validPosition = Boolean(
                                anchor && Number.isFinite(position.x) && Number.isFinite(position.y),
                            );

                            Object.assign(element.style, {
                                left: '0',
                                position: position.strategy,
                                top: '0',
                                transform: validPosition
                                    ? `translate3d(${Math.round(position.x)}px, ${Math.round(position.y)}px, 0)`
                                    : 'none',
                                visibility: validPosition ? 'visible' : 'hidden',
                                zIndex: '11000',
                            });
                        };

                        return {
                            onStart: (props) => {
                                currentProps = props;
                                component = new ReactRenderer(MentionSuggestionList, {
                                    editor: props.editor,
                                    props: { ...props, ...options, suggestionPluginKey: pluginKey },
                                });
                                unmount = props.mount(component.element, {
                                    onPosition: (position) => {
                                        if (component) {
                                            applyPosition(component.element, position);
                                        }
                                    },
                                });
                            },
                            onUpdate: (props) => {
                                currentProps = props;
                                component?.updateProps({ ...props, ...options, suggestionPluginKey: pluginKey });

                                if (!props.clientRect?.() && component) {
                                    component.element.style.visibility = 'hidden';
                                }
                            },
                            onKeyDown: ({ event }) => component?.ref?.onKeyDown(event) ?? false,
                            onExit: () => {
                                unmount?.();
                                component?.destroy();
                                component = null;
                                currentProps = null;
                            },
                        };
                    },
                }),
            ];
        },
    });
};

export const createCommentMentionNode = (options: Pick<WorkspaceMentionNodeOptions, 'resourceId' | 'resourceType'>) =>
    createWorkspaceMentionNode({
        ...options,
        hoverable: false,
        htmlAttribute: 'data-comment-mention',
        interactive: false,
        name: 'commentMention',
    });
